import React, { Suspense } from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';

import GlobalStore from '../Store/GlobalStore';
import ErrorBoundary from '../components/common/ErrorBoundary';
import { importLazyComponent } from '../components/importLazyComponent';
import Loader from '../components/loader/Loader';

/**
 * @param lazyComponentPath path to component file without ".lazy.ext" suffix
 * @param idOrClass element ID or class without "component--" prefix, or an array of IDs
 * @param loader ReactNode or null for default loading spinner
 */
function loadComponent(lazyComponentPath: string, idOrClass: string | string[], loader: React.ReactNode = null): void {
    const containers = findComponentContainers(idOrClass);

    if (containers.length) {
        const LazyComponent = React.lazy(() => importLazyComponent(lazyComponentPath));

        containers.forEach((container) => {
            ReactDOM.render(
                <ErrorBoundary fallback={<ErrorIndicator />}>
                    <Suspense fallback={loader ?? <Loader isLoading elementToShow={null} />}>
                        <Provider store={GlobalStore}>
                            {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                            <LazyComponent {...container.dataset} />
                        </Provider>
                    </Suspense>
                </ErrorBoundary>,
                container
            );
        });
    }
}

function ErrorIndicator() {
    return (
        <div className="display-1 text-center text-muted">
            <i className="far fa-times-circle" />
        </div>
    );
}

export default loadComponent;

export function findComponentContainers(idOrClass: string | string[]): HTMLElement[] {
    const getElement = (id: string) => document.getElementById(`component--${id}`) || document.getElementById(id);
    let elements: HTMLElement[] = [];

    if (Array.isArray(idOrClass)) {
        elements = idOrClass.map((id) => getElement(id)).filter((element) => element);
    } else {
        const element = getElement(idOrClass);
        elements = element ? [element] : Array.from(document.querySelectorAll(`.component--${idOrClass}`));
    }

    return elements;
}

export function renderComponent<P>(Component: React.FC<P>, idOrClass: string | string[]) {
    findComponentContainers(idOrClass).forEach((container) => {
        const component = React.createElement(Component, (container.dataset as unknown) as P);
        ReactDOM.render(<Provider store={GlobalStore}>{component}</Provider>, container);
    });
}
